/**
 * cierreCuadranteHelper.ts — Cálculos de cuadrante físico vs operativo en cierre.
 *
 * Propinas efectivo suman al esperado de gaveta pero restan en efectivo neto a entregar.
 */

export interface EsperadoGavetaInput {
  montoInicial: number;
  ventasEfectivo: number;
  ingresosEfectivo: number;
  abonosCreditoEfectivo: number;
  propinasEfectivo: number;
  retiros: number;
  gastos: number;
  devolucionesEfectivo: number;
}

export interface EsperadoGavetaResult {
  montoEsperado: number;
}

export function calcEsperadoGaveta(
  input: EsperadoGavetaInput,
): EsperadoGavetaResult {
  const montoEsperado = Math.round(
    Math.max(0, input.montoInicial) +
      Math.max(0, input.ventasEfectivo) +
      Math.max(0, input.ingresosEfectivo) +
      Math.max(0, input.abonosCreditoEfectivo) +
      Math.max(0, input.propinasEfectivo) -
      Math.max(0, input.retiros) -
      Math.max(0, input.gastos) -
      Math.max(0, input.devolucionesEfectivo),
  );
  return { montoEsperado };
}

export interface CuadranteNetoInput {
  totalContado: number;
  baseReservada: number;
  propinasEfectivo: number;
}

export interface CuadranteNetoResult {
  efectivoNetoEntregar: number;
  propinasApartar: number;
}

export function calcCuadranteNeto(
  input: CuadranteNetoInput,
): CuadranteNetoResult {
  const totalContado = Math.max(0, Math.round(input.totalContado));
  const base = Math.max(0, Math.round(input.baseReservada));
  const propinas = Math.max(0, Math.round(input.propinasEfectivo));
  const efectivoNeto = Math.max(0, totalContado - base - propinas);
  return {
    efectivoNetoEntregar: efectivoNeto,
    propinasApartar: propinas,
  };
}

export interface RendimientoOperativoInput {
  totalRecaudado: number;
  totalGastos: number;
  totalDevoluciones: number;
  totalContadoNeto: number;
  totalDigital: number;
  faltanteArqueo: number;
}

/** Hora en formato 12h con A.M. / P.M. explícito (tickets de cierre). */
export function formatCierreHourAmPm(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  let h = d.getHours();
  const m = d.getMinutes();
  const ampm = h >= 12 ? "P.M." : "A.M.";
  h = h % 12 || 12;
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")} ${ampm}`;
}

export function calcRendimientoOperativo(input: RendimientoOperativoInput) {
  const rendimientoEsperado = Math.round(
    input.totalRecaudado - input.totalGastos - input.totalDevoluciones,
  );
  const rendimientoReal = Math.round(
    input.totalContadoNeto + input.totalDigital,
  );
  return {
    rendimientoEsperado,
    rendimientoReal,
    faltanteArqueo: Math.round(input.faltanteArqueo),
  };
}

/** Fecha corta para ticket: «2 jun, 07:55 A.M.» */
export function formatCierreDateShortAmPm(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  const months = [
    "ene",
    "feb",
    "mar",
    "abr",
    "may",
    "jun",
    "jul",
    "ago",
    "sep",
    "oct",
    "nov",
    "dic",
  ];
  const day = d.getDate();
  const mon = months[d.getMonth()] ?? "???";
  return `${day} ${mon}, ${formatCierreHourAmPm(d)}`;
}

/** Encabezado ticket: «02/06/2026 09:22 A.M.» */
export function formatCierreHeaderDateAmPm(iso: string | Date): string {
  const d = typeof iso === "string" ? new Date(iso) : iso;
  const dd = String(d.getDate()).padStart(2, "0");
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const yyyy = d.getFullYear();
  return `${dd}/${mm}/${yyyy} ${formatCierreHourAmPm(d)}`;
}
